import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiShoppingCart, FiCheckCircle, FiInfo, FiChevronRight } from "react-icons/fi";
import { getStoreModules, getProfile } from "../../services/api";
import Footer from "../../components/footer";
import ModuleHeader from "../../components/moduleHeader";
import Notiflix from "notiflix";

export default function Store() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  useEffect(() => {
    Notiflix.Notify.init({
      position: "top-right",
      timeout: 3000,
      clickToClose: true,
      pauseOnHover: true,
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [modulesRes, profileRes] = await Promise.all([
          getStoreModules(),
          getProfile()
        ]);

        if (modulesRes && modulesRes.success && modulesRes.data) {
          setPackages(modulesRes.data.packages || []);
        }
        setProfileData(profileRes);
      } catch (error) {
        console.error("Veri alınırken hata:", error);
        Notiflix.Notify.failure("Bilgiler yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen min-h-screen flex flex-col items-center bg-[#F8F9FB] overflow-hidden">
      <div className="flex flex-col items-center w-full h-full sm:max-w-[390px] bg-white sm:rounded-[40px] shadow-2xl relative overflow-y-auto no-scrollbar">

        {/* Standard Module Header */}
        <div className="relative z-30 w-full mb-2">
          <ModuleHeader
            userName={profileData?.user ? `${profileData.user.first_name} ${profileData.user.last_name || ""}` : "Jodiac Kullanıcısı"}
            subtitle="Premium Modülleri Keşfedin"
            title="Mağaza"
            description="Jodiac deneyiminizi genişletmek için yeni astroloji modüllerini keşfedin. Her modül size hayatınızın farklı alanlarında derin rehberlik sunar."
            backgroundImage=""
            onExpandedChange={setIsHeaderExpanded}
            showToggle={true}
            autoHeight={true}
          />
        </div>

        {/* Dynamic Modül Listesi */}
        <div className={`w-full px-6 flex-1 bg-white relative z-20 transition-all duration-300 ${isHeaderExpanded ? "mt-0" : "-mt-6 rounded-t-[40px] shadow-[0_-20px_40px_rgba(0,0,0,0.05)]"}`}>
          <div className="pt-8 pb-32">
            {packages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-400">
                  <FiInfo size={32} />
                </div>
                <p className="text-gray-500 font-medium">Şu anda sunulan modül bulunamadı.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {packages.map((pkg, index) => (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    key={pkg.id}
                    onClick={() => !pkg.is_owned && navigate(`/${id}/store/${pkg.package_id}`)}
                    className={`relative group cursor-pointer rounded-[1.75rem] p-4 transition-all duration-300 ${pkg.is_owned
                      ? "bg-gray-50/80 border border-gray-100"
                      : "bg-white border border-gray-100 hover:border-[#3DA7B5]/30 hover:shadow-xl hover:shadow-[#3DA7B5]/5"
                      }`}
                  >
                    <div className="flex gap-4 items-center">
                      {/* Görsel Alanı */}
                      <div className="relative w-20 h-20 shrink-0">
                        <img
                          src={pkg.image_url || "/assets/pngImages/defaultModule.png"}
                          alt={pkg.name}
                          className={`w-full h-full rounded-2xl object-cover shadow-sm transition-all duration-500 ${pkg.is_owned ? "grayscale opacity-50" : "group-hover:scale-105"
                            }`}
                        />
                        {!pkg.is_owned && (
                          <div className="absolute -bottom-2 -right-2 bg-white text-[#3DA7B5] p-1.5 rounded-xl shadow-sm border border-gray-50">
                            <FiShoppingCart size={14} />
                          </div>
                        )}
                      </div>

                      {/* İçerik Alanı */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className={`text-[16px] font-black leading-tight mb-1 truncate ${pkg.is_owned ? "text-gray-400" : "text-gray-900"
                              }`}>
                              {pkg.name}
                            </h3>
                            <div className="flex items-center gap-2">
                              {pkg.is_owned ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#3DA7B5] bg-[#3DA7B5]/5 px-2 py-0.5 rounded-full">
                                  <FiCheckCircle size={10} /> Sende Var
                                </span>
                              ) : (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[14px] font-black text-[#3DA7B5]">
                                    {pkg.jodiac_token_cost} JT
                                  </span>
                                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Maliyet</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {!pkg.is_owned && (
                            <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-300 group-hover:bg-[#3DA7B5]/10 group-hover:text-[#3DA7B5] transition-colors">
                              <FiChevronRight size={18} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer uuid={id} />
    </div>
  );
}

